import {
  Attribute,
  TemporalConstraintsEnumLegacy,
  DerivedEventFilterType,
  DerivedEventType,
  Entity,
  SimpleEventFilterType,
  SimpleEventType,
  NumericFiltersAttributesEnumLegacy,
  SubjectVariableFilterType,
} from '@verantos-dev/types-cohorts';
import { ConceptDomain } from '@verantos-dev/types-cohorts';

export enum ValueType {
  DAYS = 'days',
  INTEGER = 'integer',
  FLOAT = 'float',
  CONCEPT = 'concept',
}

const NUMERIC_OPTIONS = [
  { value: '>=', displayValue: '>=' },
  { value: '<=', displayValue: '<=' },
  { value: '>', displayValue: '>' },
  { value: '<', displayValue: '<' },
  { value: '=', displayValue: '=' },
  { value: '!=', displayValue: '!=' },
];

export default {
  simpleEvents: {
    entities: [
      { value: SimpleEventType.CONDITION_OCCURRENCE, displayValue: 'Condition occurrence' },
      { value: SimpleEventType.DRUG_EXPOSURE, displayValue: 'Drug exposure' },
      { value: SimpleEventType.MEASUREMENT, displayValue: 'Measurement' },
      { value: SimpleEventType.OBSERVATION, displayValue: 'Observation' },
      { value: SimpleEventType.PROCEDURE_OCCURRENCE, displayValue: 'Procedure occurrence' },
      { value: SimpleEventType.VISIT_OCCURRENCE, displayValue: 'Visit occurrence' },
    ],
    attributes: {
      [SimpleEventType.CONDITION_OCCURRENCE]: {
        options: [
          {
            value: Attribute.CONDITION_CONCEPT,
            displayValue: 'Condition concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.CONDITION_TYPE_CONCEPT,
            displayValue: 'Condition type concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.CONDITION_STATUS_CONCEPT,
            displayValue: 'Condition status concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.CONDITION_SOURCE_CONCEPT,
            displayValue: 'Condition source concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.CONDITION_START_DATE,
            displayValue: 'Condition start date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
          {
            value: Attribute.CONDITION_END_DATE,
            displayValue: 'Condition end date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
          {
            value: Attribute.VISIT_CONCEPT,
            displayValue: 'Visit concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_SOURCE_CONCEPT,
            displayValue: 'Visit source concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_TYPE_CONCEPT,
            displayValue: 'Visit type concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.SPECIALTY_CONCEPT,
            displayValue: 'Provider specialty concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.PROVIDER,
          },
        ],
      },
      [SimpleEventType.DRUG_EXPOSURE]: {
        options: [
          {
            value: Attribute.DRUG_CONCEPT,
            displayValue: 'Drug concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.DRUG_TYPE_CONCEPT,
            displayValue: 'Drug type concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.DRUG_SOURCE_CONCEPT,
            displayValue: 'Drug source concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.ROUTE_CONCEPT,
            displayValue: 'Route concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.VISIT_CONCEPT,
            displayValue: 'Visit concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_SOURCE_CONCEPT,
            displayValue: 'Visit source concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_TYPE_CONCEPT,
            displayValue: 'Visit type concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.SPECIALTY_CONCEPT,
            displayValue: 'Provider specialty concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.PROVIDER,
          },
          {
            value: Attribute.REFILLS,
            displayValue: 'Refills',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.INTEGER,
          },
          {
            value: Attribute.DAYS_SUPPLY,
            displayValue: 'Days supply',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.INTEGER,
          },
          {
            value: Attribute.QUANTITY,
            displayValue: 'Quantity',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.FLOAT,
          },
          {
            value: Attribute.DRUG_EXPOSURE_START_DATE,
            displayValue: 'Drug exposure start date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
          {
            value: Attribute.DRUG_EXPOSURE_END_DATE,
            displayValue: 'Drug exposure end date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
        ],
      },
      [SimpleEventType.MEASUREMENT]: {
        options: [
          {
            value: Attribute.MEASUREMENT_CONCEPT,
            displayValue: 'Measurement concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.MEASUREMENT_TYPE_CONCEPT,
            displayValue: 'Measurement type concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.MEASUREMENT_SOURCE_CONCEPT,
            displayValue: 'Measurement source concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.UNIT_CONCEPT,
            displayValue: 'Unit concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.VALUE_AS_CONCEPT,
            displayValue: 'Value as concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.MEASUREMENT_DATE,
            displayValue: 'Measurement date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
          {
            value: Attribute.VISIT_CONCEPT,
            displayValue: 'Visit concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_SOURCE_CONCEPT,
            displayValue: 'Visit source concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_TYPE_CONCEPT,
            displayValue: 'Visit type concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.SPECIALTY_CONCEPT,
            displayValue: 'Provider specialty concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.PROVIDER,
          },
          {
            value: Attribute.RANGE_HIGH,
            displayValue: 'Range high',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.FLOAT,
          },
          {
            value: Attribute.RANGE_LOW,
            displayValue: 'Range low',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.FLOAT,
          },
          {
            value: Attribute.VALUE_AS_NUMBER,
            displayValue: 'Value as number',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.FLOAT,
          },
        ],
      },
      [SimpleEventType.OBSERVATION]: {
        options: [
          {
            value: Attribute.OBSERVATION_CONCEPT,
            displayValue: 'Observation concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.OBSERVATION_TYPE_CONCEPT,
            displayValue: 'Observation type concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.VALUE_AS_CONCEPT,
            displayValue: 'Value as concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.UNIT_CONCEPT,
            displayValue: 'Unit concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.VISIT_CONCEPT,
            displayValue: 'Visit concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_SOURCE_CONCEPT,
            displayValue: 'Visit source concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_TYPE_CONCEPT,
            displayValue: 'Visit type concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.SPECIALTY_CONCEPT,
            displayValue: 'Provider specialty concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.PROVIDER,
          },
          {
            value: Attribute.VALUE_AS_NUMBER,
            displayValue: 'Value as number',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.FLOAT,
          },
          {
            value: Attribute.OBSERVATION_DATE,
            displayValue: 'Observation date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
        ],
      },
      [SimpleEventType.PROCEDURE_OCCURRENCE]: {
        options: [
          {
            value: Attribute.PROCEDURE_CONCEPT,
            displayValue: 'Procedure concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.PROCEDURE_TYPE_CONCEPT,
            displayValue: 'Procedure type concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.MODIFIER_CONCEPT,
            displayValue: 'Modifier concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.PROCEDURE_DATE,
            displayValue: 'Procedure date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
          {
            value: Attribute.VISIT_CONCEPT,
            displayValue: 'Visit concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_SOURCE_CONCEPT,
            displayValue: 'Visit source concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.VISIT_TYPE_CONCEPT,
            displayValue: 'Visit type concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.VISIT_OCCURRENCE,
          },
          {
            value: Attribute.SPECIALTY_CONCEPT,
            displayValue: 'Provider specialty concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.PROVIDER,
          },
          {
            value: Attribute.QUANTITY,
            displayValue: 'Quantity',
            filterType: SimpleEventFilterType.NUMERIC,
            valueType: ValueType.FLOAT,
          },
        ],
      },
      [SimpleEventType.VISIT_OCCURRENCE]: {
        options: [
          {
            value: Attribute.VISIT_CONCEPT,
            displayValue: 'Visit concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.VISIT_SOURCE_CONCEPT,
            displayValue: 'Visit source concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.VISIT_TYPE_CONCEPT,
            displayValue: 'Visit type concept',
            filterType: SimpleEventFilterType.CONCEPT,
            valueType: ValueType.CONCEPT,
          },
          {
            value: Attribute.CONDITION_CONCEPT,
            displayValue: 'Condition concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.CONDITION_OCCURRENCE,
          },
          {
            value: Attribute.CONDITION_TYPE_CONCEPT,
            displayValue: 'Condition type concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.CONDITION_OCCURRENCE,
          },
          {
            value: Attribute.CONDITION_STATUS_CONCEPT,
            displayValue: 'Condition status concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.CONDITION_OCCURRENCE,
          },
          {
            value: Attribute.CONDITION_SOURCE_CONCEPT,
            displayValue: 'Condition source concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.CONDITION_OCCURRENCE,
          },
          {
            value: Attribute.SPECIALTY_CONCEPT,
            displayValue: 'Provider specialty concept',
            filterType: SimpleEventFilterType.RELATED_ENTITY,
            valueType: ValueType.CONCEPT,
            entity: Entity.PROVIDER,
          },
          {
            value: Attribute.VISIT_START_DATE,
            displayValue: 'Visit start date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
          {
            value: Attribute.VISIT_END_DATE,
            displayValue: 'Visit end date',
            filterType: SimpleEventFilterType.UNIQUENESS,
            valueType: ValueType.DAYS,
          },
        ],
      },
    },
    operators: {
      default: [],
      [ValueType.CONCEPT]: [{ value: 'IN', displayValue: 'In' }],
      [ValueType.DAYS]: [
        { value: 'FIXED_WINDOW', displayValue: 'Fixed window' },
        { value: 'ROLLING_WINDOW', displayValue: 'Rolling window' },
      ],
      [ValueType.FLOAT]: NUMERIC_OPTIONS,
      [ValueType.INTEGER]: NUMERIC_OPTIONS,
    },
  },
  conceptGroupFilters: {
    [Attribute.PLACE_OF_SERVICE_CONCEPT]: {
      default: {
        include: [],
        exclude: [],
      },
    },
    [Attribute.CONDITION_CONCEPT]: {
      default: {
        include: [ConceptDomain.CONDITION],
        exclude: [],
      },
    },
    [Attribute.CONDITION_TYPE_CONCEPT]: {
      default: {
        include: [ConceptDomain.TYPE_CONCEPT],
        exclude: [],
      },
    },
    [Attribute.CONDITION_STATUS_CONCEPT]: {
      default: {
        include: [ConceptDomain.CONDITION_STATUS],
        exclude: [],
      },
    },
    [Attribute.CONDITION_SOURCE_CONCEPT]: {
      default: {
        include: [ConceptDomain.CONDITION],
        exclude: [],
      },
    },
    [Attribute.DRUG_CONCEPT]: {
      default: {
        include: [ConceptDomain.DRUG],
        exclude: [],
      },
    },
    [Attribute.DRUG_TYPE_CONCEPT]: {
      default: {
        include: [ConceptDomain.TYPE_CONCEPT],
        exclude: [],
      },
    },
    [Attribute.ROUTE_CONCEPT]: {
      default: {
        include: [ConceptDomain.ROUTE],
        exclude: [],
      },
    },
    [Attribute.DRUG_SOURCE_CONCEPT]: {
      default: {
        include: [ConceptDomain.DRUG],
        exclude: [],
      },
    },
    [Attribute.MEASUREMENT_CONCEPT]: {
      default: {
        include: [ConceptDomain.MEASUREMENT],
        exclude: [],
      },
    },
    [Attribute.MEASUREMENT_SOURCE_CONCEPT]: {
      default: {
        include: [ConceptDomain.MEASUREMENT],
        exclude: [],
      },
    },
    [Attribute.MEASUREMENT_TYPE_CONCEPT]: {
      default: {
        include: [ConceptDomain.TYPE_CONCEPT],
        exclude: [],
      },
    },
    [Attribute.VALUE_AS_CONCEPT]: {
      default: {
        include: [],
        exclude: [],
      },
      [SimpleEventType.MEASUREMENT]: {
        include: [ConceptDomain.MEAS_VALUE],
        exclude: [],
      },
    },
    [Attribute.UNIT_CONCEPT]: {
      default: {
        include: [ConceptDomain.UNIT],
        exclude: [],
      },
    },
    [Attribute.UNIT_SOURCE_CONCEPT]: {
      default: {
        include: [ConceptDomain.UNIT],
        exclude: [],
      },
    },
    [Attribute.OBSERVATION_CONCEPT]: {
      default: {
        include: [],
        exclude: [
          ConceptDomain.CONDITION,
          ConceptDomain.PROCEDURE,
          ConceptDomain.DRUG,
          ConceptDomain.MEASUREMENT,
          ConceptDomain.DEVICE,
        ],
      },
    },
    [Attribute.OBSERVATION_TYPE_CONCEPT]: {
      default: {
        include: [ConceptDomain.TYPE_CONCEPT],
        exclude: [],
      },
    },
    [Attribute.GENDER_CONCEPT]: {
      default: {
        include: [ConceptDomain.GENDER],
        exclude: [],
      },
    },
    [Attribute.RACE_CONCEPT]: {
      default: {
        include: [ConceptDomain.RACE],
        exclude: [],
      },
    },
    [Attribute.ETHNICITY_CONCEPT]: {
      default: {
        include: [ConceptDomain.ETHNICITY],
        exclude: [],
      },
    },
    [Attribute.PROCEDURE_CONCEPT]: {
      default: {
        include: [ConceptDomain.PROCEDURE],
        exclude: [],
      },
    },
    [Attribute.PROCEDURE_TYPE_CONCEPT]: {
      default: {
        include: [ConceptDomain.TYPE_CONCEPT],
        exclude: [],
      },
    },
    [Attribute.MODIFIER_CONCEPT]: {
      default: {
        include: [],
        exclude: [],
      },
    },
    [Attribute.SPECIALTY_CONCEPT]: {
      default: {
        include: [ConceptDomain.PROVIDER],
        exclude: [],
      },
    },
    [Attribute.VISIT_CONCEPT]: {
      default: {
        include: [ConceptDomain.VISIT],
        exclude: [],
      },
    },
    [Attribute.VISIT_SOURCE_CONCEPT]: {
      default: {
        include: [ConceptDomain.VISIT],
        exclude: [],
      },
    },
    [Attribute.VISIT_TYPE_CONCEPT]: {
      default: {
        include: [ConceptDomain.TYPE_CONCEPT],
        exclude: [],
      },
    },
  },
  derivedEvents: {
    attributes: {
      [SimpleEventType.CONDITION_OCCURRENCE]: [
        {
          value: Attribute.CONDITION_START_DATE,
          displayValue: 'Condition start date',
        },
        {
          value: Attribute.CONDITION_END_DATE,
          displayValue: 'Condition end date',
        },
      ],
      [SimpleEventType.DRUG_EXPOSURE]: [
        {
          value: Attribute.DRUG_EXPOSURE_START_DATE,
          displayValue: 'Drug exposure start date',
        },
        {
          value: Attribute.DRUG_EXPOSURE_END_DATE,
          displayValue: 'Drug exposure end date',
        },
      ],
      [SimpleEventType.MEASUREMENT]: [
        {
          value: Attribute.MEASUREMENT_DATE,
          displayValue: 'Measurement date',
        },
      ],
      [SimpleEventType.OBSERVATION]: [
        {
          value: Attribute.OBSERVATION_DATE,
          displayValue: 'Observation date',
        },
      ],
      [SimpleEventType.PROCEDURE_OCCURRENCE]: [
        {
          value: Attribute.PROCEDURE_DATE,
          displayValue: 'Procedure date',
        },
      ],
      [SimpleEventType.VISIT_OCCURRENCE]: [
        {
          value: Attribute.VISIT_START_DATE,
          displayValue: 'Visit start date',
        },
        {
          value: Attribute.VISIT_END_DATE,
          displayValue: 'Visit end date',
        },
      ],
      position: [
        { value: 'MIN', displayValue: 'First' },
        { value: 'INDEX', displayValue: 'Nth' },
        { value: 'MAX', displayValue: 'Last' },
      ],
      [DerivedEventType.INSTANCE_EVENT]: [
        {
          value: Attribute.DERIVED_EVENT_DATE,
          displayValue: 'Derived event date',
        },
      ],
      [DerivedEventType.MERGED_EVENT]: [
        {
          value: Attribute.DERIVED_EVENT_DATE,
          displayValue: 'Derived event date',
        },
      ],
      [DerivedEventType.SEQUENCE_EVENT]: [
        {
          value: Attribute.DERIVED_EVENT_DATE,
          displayValue: 'Derived event date',
        },
      ],
      [DerivedEventFilterType.TEMPORAL]: [
        {
          value: TemporalConstraintsEnumLegacy.MINIMUM_BETWEEN,
          displayValue: 'Minimum time between occurrences',
        },
        {
          value: TemporalConstraintsEnumLegacy.MAXIMUM_BETWEEN,
          displayValue: 'Maximum time between occurrences',
        },
        {
          value: TemporalConstraintsEnumLegacy.TOTAL_BETWEEN,
          displayValue: 'Maximum time between first and last occurrence',
        },
      ],
      [DerivedEventFilterType.EVENT]: [],
      [DerivedEventFilterType.UNIQUENESS]: [
        { value: 'FIXED_WINDOW', displayValue: 'Fixed window' },
        { value: 'ROLLING_WINDOW', displayValue: 'Rolling window' },
      ],
    },
  },
  subjectVariables: {
    attributes: {
      [SubjectVariableFilterType.NUMERIC]: {
        options: [
          {
            value: NumericFiltersAttributesEnumLegacy.YEAR_OF_BIRTH,
            displayValue: 'Year of birth',
          },
          {
            value: NumericFiltersAttributesEnumLegacy.MONTH_OF_BIRTH,
            displayValue: 'Month of birth',
          },
          {
            value: NumericFiltersAttributesEnumLegacy.DAY_OF_BIRTH,
            displayValue: 'Day of birth',
          },
          {
            value: NumericFiltersAttributesEnumLegacy.AGE_AT_INDEX,
            displayValue: 'Age at index',
          },
        ],
      },
      [SubjectVariableFilterType.CONCEPT]: {
        options: [
          {
            value: Attribute.GENDER_CONCEPT,
            displayValue: 'Gender concept',
          },
          {
            value: Attribute.RACE_CONCEPT,
            displayValue: 'Race concept',
          },
          {
            value: Attribute.ETHNICITY_CONCEPT,
            displayValue: 'Ethnicity concept',
          },
        ],
      },
      [SubjectVariableFilterType.COUNT]: {
        options: [
          {
            value: 'COUNT',
            displayValue: 'Count',
          },
        ],
      },
    },
    operators: {
      [SubjectVariableFilterType.NUMERIC]: NUMERIC_OPTIONS,
      [SubjectVariableFilterType.CONCEPT]: [{ value: 'IN', displayValue: 'In' }],
      [SubjectVariableFilterType.COUNT]: NUMERIC_OPTIONS,
    },
  },
  indexDate: {
    position: [
      { value: 'FIRST', displayValue: 'First' },
      { value: 'NTH', displayValue: 'Nth' },
      { value: 'LAST', displayValue: 'Last' },
    ],
  },
};
